import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../services/AuthContext';
import { ArrowLeft, Eye, Save, Send, AlertTriangle } from 'lucide-react';

// Correos automáticos: plantillas de bienvenida y del curso de PRL, avisos a
// administración e historial de envíos. Está dentro de Nuevos Empleados porque la sección de Configuración
// no se muestra. Solo existe con el envío de correo activado.

const ESTADOS = {
  pendiente: ['Pendiente', 'warning'], enviado: ['Enviado', 'info'],
  entregado: ['Entregado', 'success'], rebotado: ['Rebotado', 'danger'],
  fallido: ['Fallido', 'danger'],
};
const TIPOS = {
  bienvenida: 'Bienvenida', aviso_alta: 'Aviso de alta completada',
  recuperacion_contrasena: 'Recuperación de contraseña', prueba: 'Prueba de plantilla',
  prl: 'Curso de PRL', prueba_prl: 'Prueba de plantilla de PRL',
};
const CORREO_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fmtFechaHora = (d) => d ? new Date(d).toLocaleString('es-ES', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '';

function Plantilla({ clave, titulo, ayuda }) {
  const [plantilla, setPlantilla] = useState(null);
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [errores, setErrores] = useState([]);
  const [vista, setVista] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [emailPrueba, setEmailPrueba] = useState('');
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const cuerpoRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const res = await adminApi.getPlantilla(clave);
      setPlantilla(res.data);
      setAsunto(res.data.asunto);
      setCuerpo(res.data.cuerpo);
    } catch { toast.error('No se pudo cargar la plantilla.'); }
  }, [clave]);
  useEffect(() => { cargar(); }, [cargar]);

  if (!plantilla) return <div className="spinner" />;
  const cambiada = asunto !== plantilla.asunto || cuerpo !== plantilla.cuerpo;

  // Inserta el marcador donde está el cursor del texto.
  const insertar = (clave) => {
    const el = cuerpoRef.current;
    const marcador = `{${clave}}`;
    const ini = el ? el.selectionStart : cuerpo.length;
    const fin = el ? el.selectionEnd : cuerpo.length;
    setCuerpo(cuerpo.slice(0, ini) + marcador + cuerpo.slice(fin));
    setTimeout(() => {
      if (el) { el.focus(); el.selectionStart = el.selectionEnd = ini + marcador.length; }
    }, 0);
  };

  const verVistaPrevia = async () => {
    try {
      const res = await adminApi.vistaPreviaPlantilla(clave, { asunto, cuerpo });
      setErrores(res.data.errores);
      setVista(res.data);
    } catch { toast.error('No se pudo generar la vista previa.'); }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await adminApi.guardarPlantilla(clave, { asunto, cuerpo });
      setPlantilla(res.data);
      setAsunto(res.data.asunto);
      setErrores([]);
      toast.success('Plantilla guardada.');
    } catch (err) {
      setErrores(err.response?.data?.errores || []);
      toast.error('La plantilla tiene errores y no se ha guardado.');
    }
    setGuardando(false);
  };

  const enviarPrueba = async () => {
    const email = emailPrueba.trim();
    if (!CORREO_RE.test(email)) { toast.error('La dirección de correo no es válida.'); return; }
    setEnviandoPrueba(true);
    try {
      const res = await adminApi.enviarPruebaPlantilla(clave, email);
      if (res.data.estado === 'enviado') toast.success(`Prueba enviada a ${email}.`);
      else toast.error(`No se ha podido enviar la prueba: ${res.data.ultimo_error || 'error desconocido'}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Error al enviar la prueba.'); }
    setEnviandoPrueba(false);
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <h3 style={{ marginTop: 0 }}>{titulo}</h3>
      {ayuda && <p style={{ fontSize: '0.84rem', color: 'var(--gray-500)', marginTop: -4 }}>{ayuda}</p>}

      {plantilla.es_provisional && (
        <div className="alert alert--warning" style={{ fontSize: '0.86rem' }}>
          <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> La plantilla es
          <strong> provisional</strong>: mientras contenga la marca «[PROVISIONAL», el correo
          no se envía a los candidatos. Sí se pueden hacer vistas previas y envíos de prueba.
        </div>
      )}

      <div className="form-group">
        <label>Asunto</label>
        <input className="form-input" value={asunto} maxLength={200}
          onChange={e => setAsunto(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Texto del correo</label>
        <textarea ref={cuerpoRef} className="form-input" rows={16} value={cuerpo}
          onChange={e => setCuerpo(e.target.value)}
          style={{ fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical' }} />
      </div>

      <div style={{ fontSize: '0.82rem', marginBottom: 12 }}>
        <strong>Marcadores</strong> (pulsa para insertarlo; los marcados con * son obligatorios):
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {plantilla.marcadores.map(m => (
            <button key={m.clave} type="button" className="btn btn--secondary btn--sm"
              title={m.descripcion} onClick={() => insertar(m.clave)}>
              {`{${m.clave}}`}{m.obligatorio ? ' *' : ''}
            </button>
          ))}
        </div>
        <div style={{ color: 'var(--gray-500)', marginTop: 6 }}>
          {plantilla.marcadores.map(m => `{${m.clave}}: ${m.descripcion.toLowerCase()}`).join(' · ')}
        </div>
      </div>

      {errores.length > 0 && (
        <div className="alert alert--danger" style={{ fontSize: '0.84rem' }}>
          {errores.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn--secondary" onClick={verVistaPrevia}><Eye size={14} /> Vista previa</button>
        <button className="btn btn--primary" onClick={guardar} disabled={guardando || !cambiada}>
          <Save size={14} /> {guardando ? 'Guardando...' : 'Guardar'}
        </button>
        {cambiada && <span style={{ fontSize: '0.8rem', color: 'var(--amber-500)' }}>Cambios sin guardar</span>}
        {plantilla.actualizado && (
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            Guardada el {fmtFechaHora(plantilla.actualizado)}{plantilla.actualizado_por ? ` por ${plantilla.actualizado_por}` : ''}
          </span>
        )}
      </div>

      {vista && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginBottom: 4 }}>
            Vista previa con datos de ejemplo (no se ha guardado)
          </div>
          <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', background: 'var(--gray-50)', fontSize: '0.88rem' }}>
              <strong>Asunto:</strong> {vista.asunto}
            </div>
            {/* El HTML llega escapado del servidor; además, sin scripts. */}
            <iframe title="Vista previa del correo" srcDoc={vista.html} sandbox=""
              style={{ width: '100%', height: 380, border: 0, background: 'white' }} />
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--gray-200)' }}>
        <label style={{ fontSize: '0.86rem', fontWeight: 600 }}>Enviar una prueba</label>
        <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', margin: '4px 0 8px' }}>
          Se envía la plantilla <strong>guardada</strong>, con datos de ejemplo y el asunto
          precedido de «[PRUEBA]».
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input className="form-input" type="email" style={{ flex: '1 1 260px' }}
            placeholder="tu.correo@ejemplo.com" value={emailPrueba}
            onChange={e => setEmailPrueba(e.target.value)} />
          <button className="btn btn--secondary" onClick={enviarPrueba}
            disabled={enviandoPrueba || cambiada}
            title={cambiada ? 'Guarda los cambios antes de enviar una prueba' : ''}>
            <Send size={14} /> {enviandoPrueba ? 'Enviando...' : 'Enviar prueba'}
          </button>
        </div>
        {cambiada && (
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>
            Guarda los cambios antes de enviar una prueba.
          </div>
        )}
      </div>
    </div>
  );
}

function Avisos() {
  const [activos, setActivos] = useState(null);
  useEffect(() => {
    adminApi.getAjustesCorreo()
      .then(res => setActivos(res.data.avisos_alta_activos))
      .catch(() => toast.error('No se pudieron cargar los ajustes.'));
  }, []);

  const cambiar = async (valor) => {
    try {
      const res = await adminApi.updateAjustesCorreo({ avisos_alta_activos: valor });
      setActivos(res.data.avisos_alta_activos);
      toast.success(valor ? 'Avisos activados.' : 'Avisos desactivados.');
    } catch { toast.error('No se pudo guardar.'); }
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <h3 style={{ marginTop: 0 }}>Avisos a administración</h3>
      {activos === null ? <div className="spinner" /> : (
        <label style={{ fontSize: '0.9rem' }}>
          <input type="checkbox" checked={activos} onChange={e => cambiar(e.target.checked)}
            style={{ marginRight: 8 }} />
          Avisar por correo cuando un empleado complete su alta
        </label>
      )}
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', margin: '8px 0 0' }}>
        El aviso llega a todas las cuentas de administración que tengan correo registrado.
      </p>
    </div>
  );
}

function Historial() {
  const [datos, setDatos] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');

  const cargar = useCallback(async () => {
    try {
      const params = { page: pagina };
      if (estado) params.estado = estado;
      if (tipo) params.tipo = tipo;
      const res = await adminApi.getEnvios(params);
      setDatos(res.data);
    } catch { toast.error('No se pudo cargar el historial.'); }
  }, [pagina, estado, tipo]);
  useEffect(() => { cargar(); }, [cargar]);

  const filtrar = (fn) => (e) => { setPagina(1); fn(e.target.value); };

  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Historial de envíos</h3>
      <div className="filter-bar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select className="form-input" style={{ maxWidth: 220 }} value={estado} onChange={filtrar(setEstado)}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADOS).map(([k, [t]]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <select className="form-input" style={{ maxWidth: 260 }} value={tipo} onChange={filtrar(setTipo)}>
          <option value="">Todos los tipos</option>
          {Object.entries(TIPOS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
      </div>
      {!datos ? <div className="spinner" /> : datos.results.length === 0 ? (
        <div className="empty-state"><p>No hay envíos</p></div>
      ) : (
        <>
          <table className="data-table">
            <thead><tr>
              <th>Fecha</th><th>Tipo</th><th>Solicitud</th><th>Destinatario</th>
              <th>Estado</th><th>Intentos</th><th>Último error</th>
            </tr></thead>
            <tbody>
              {datos.results.map(e => {
                const [texto, color] = ESTADOS[e.estado] || [e.estado, 'neutral'];
                return (
                  <tr key={e.id}>
                    <td>{fmtFechaHora(e.creado)}</td>
                    <td>{e.tipo_display}</td>
                    <td>{e.submission ? `nº ${e.submission}` : '—'}</td>
                    <td>{e.destinatario}</td>
                    <td><span className={`badge badge--${color}`}>{texto}</span></td>
                    <td>{e.intentos}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--gray-500)', maxWidth: 280 }}>{e.ultimo_error || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 10 }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{datos.count} envíos</span>
            <button className="btn btn--secondary btn--sm" disabled={!datos.previous} onClick={() => setPagina(pagina - 1)}>Anterior</button>
            <button className="btn btn--secondary btn--sm" disabled={!datos.next} onClick={() => setPagina(pagina + 1)}>Siguiente</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminCorreo() {
  const { envioCorreoActivo } = useAuth();
  if (!envioCorreoActivo) return <Navigate to="/gestion/solicitudes" replace />;

  return (
    <div>
      <div className="page-header">
        <Link to="/gestion/solicitudes" className="btn btn--ghost btn--sm" style={{ marginBottom: 8 }}>
          <ArrowLeft size={14} /> Nuevos Empleados
        </Link>
        <h1>Correos automáticos</h1>
        <p>Textos de los correos que reciben los candidatos, avisos e historial de envíos</p>
      </div>
      <Plantilla clave="bienvenida" titulo="Plantilla del correo de bienvenida"
        ayuda="Sale al crear el alta, con el enlace personal y la contraseña." />
      <Plantilla clave="prl" titulo="Plantilla del correo del curso de PRL"
        ayuda="Sale una sola vez, cuando el empleado aprueba los exámenes y llega al último paso. Las respuestas van a sae@esmcd.com." />
      <Avisos />
      <Historial />
    </div>
  );
}
